import React, { useMemo } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import CoinIcon from '@assets/images/Coin.svg';
import { useAdultProfile } from '@modules/adultProfile/duck/hooks';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { usePersonPaymentLog } from '@modules/paymentLogs/duck/hooks';
import { setUserGuid } from '@modules/paymentLogs/duck/slice';
import { profileSel } from '@modules/user';
import {
  isMemberDetailsLoadingSel,
  memberDetailsSel,
} from '@modules/youthProfile/duck/selectors';
import { Button, Card, Spin, T } from '@shared/components';
import { hasNewPermissionSel } from '@shared/duck/selectors/general.selectors';
import { newPermissions } from '@shared/newPermissions';

import {
  goToUserPaymentLogs,
  overrideShowUnitPaymentLogsSel,
} from '../../duck';
import Amount from '../common/Amount';
import styles from './PaymentLogWidget.less';

interface PaymentLogWidgetProps {
  isAdult?: boolean;
  isMyProfile?: boolean;
  isMyYouthProfile?: boolean;
}

const PaymentLogWidget: React.FC<PaymentLogWidgetProps> = ({
  isAdult = false,
  isMyProfile = false,
}) => {
  const dispatch = useDispatch();
  const { balanceDetails } = usePersonPaymentLog();

  const balance = useMemo(() => balanceDetails?.balance ?? 0, [balanceDetails]);
  const memberDetails = useSelector(memberDetailsSel);
  const memberDetailsLoading = useSelector(isMemberDetailsLoadingSel);
  const myProfile = useSelector(profileSel);

  const { sbProfileData } = useAdultProfile();

  const { loading: personLogsLoadings } = usePersonPaymentLog();

  const loading = useMemo(() => {
    if (isMyProfile) return false;
    const detailsLoading = isAdult
      ? sbProfileData.isLoading
      : memberDetailsLoading;
    return personLogsLoadings || detailsLoading;
  }, [
    isMyProfile,
    isAdult,
    sbProfileData,
    memberDetailsLoading,
    personLogsLoadings,
  ]);

  const onPress = () => {
    let selectedGuid = '';
    if (isMyProfile) {
      selectedGuid = myProfile?.personGuid;
    } else if (isAdult) {
      selectedGuid = sbProfileData?.data?.profile?.personGuid ?? '';
    } else {
      selectedGuid = memberDetails?.profile?.personGuid;
    }
    dispatch(setUserGuid({ personGuid: selectedGuid }));
    dispatch(goToUserPaymentLogs({ providedPersonGuid: selectedGuid }));
  };

  return (
    <Card className={styles.card} margin noPadding shadow>
      <Spin delay={100} size="large" spinning={loading}>
        <T bold colored size="4">
          <FormattedMessage id="paymentLogWidget.title" />
        </T>
        <div className={styles.content}>
          <div className={styles.cointContainer}>
            <CoinIcon className={styles.coin} />
          </div>
          <div className={styles.amountContainer}>
            <T size="3">
              <FormattedMessage id="paymentLogWidget.balance" />
            </T>
            <Amount amount={balance} bold errorIndicator size="1" />
          </div>
          <Button
            color="info"
            onClick={onPress}
            shape="round"
            size="small"
            uppercase
          >
            <FormattedMessage id="paymentLogWidget.viewPaymentLogs" />
          </Button>
        </div>
      </Spin>
    </Card>
  );
};

const PaymentLogWidgetContainer: React.FC<PaymentLogWidgetProps> = ({
  isAdult,
  isMyProfile,
  isMyYouthProfile,
}) => {
  const overrideFlag = useSelector(overrideShowUnitPaymentLogsSel);

  const flagEnabled = featureFlags.getFlag('SHOW_PAYMENT_LOGS') || overrideFlag;
  const canEditIndivLogs = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.CRUD_INDIV_LOG),
  );

  if (!flagEnabled || (!isMyProfile && !canEditIndivLogs && !isMyYouthProfile))
    return null;

  return <PaymentLogWidget isAdult={isAdult} isMyProfile={isMyProfile} />;
};

export default PaymentLogWidgetContainer;
